import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGetRolesQuery, useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";
import { setAccessPermissions, setAccessRoleNames } from "state/slices/auth/accessPermissionsSlice";
import { setSessionExpiredReason } from "state/slices/sessionSlice";
import { PERMISSIONS_POLL_INTERVAL_MS } from "utils/constants";
import { permissionSetGrants, roleGrantsPermission } from "utils/accessManagement/roleDisplay";

// Sorted-array comparison, used to detect a real role change rather than just
// a different-but-equivalent array reference from a fresh RTK Query response.
const sameRoleNames = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((name, i) => name === sortedB[i]);
};

// Loads the signed-in administrator's effective Arbiter Auth permissions:
// 1. GET /assigned/user/roles -> the user's global role names (e.g. ["OperationsAdmin"])
// 2. GET /roles -> every role's own permissions[] (already fetched/cached
//    elsewhere in the app, so this is normally a cache hit, not a new request)
// 3. Union the permissions[] of every role the user holds — this is the
//    user's real, backend-defined permission set. A role's own permissions
//    array (including any auth:* wildcard) is the single source of truth;
//    nothing here hardcodes which roles are "admin enough" for what.
//
// Re-fetched on a poll interval and on reconnect (via setupListeners in
// store.js), not just on mount — otherwise a role change made by an admin
// elsewhere is invisible to an already-open tab that never happens to trigger
// another API call, and the UI would keep rendering the old, now-wrong
// permission set indefinitely (up to the 1-hour hard session ceiling).
// Deliberately NOT refetchOnFocus: RTK Query's focus listener fires on the
// window's focus/visibilitychange events, which MUI's portaled components
// (Select, Dialog, Autocomplete, Accordion — all over this app) can trigger
// on ordinary in-page interaction, not just real tab/window switches — e.g.
// clicking to expand a row in RoleAssignmentsPage's grid was observed
// re-firing this on every click. The 5-minute poll already bounds how stale
// this can get; refetchOnFocus is not worth the extra request-per-click.
export const useAccessPermissions = () => {
  const dispatch = useDispatch();
  const email = useSelector((state) => state.auth?.userAccount?.username);
  const previousRoleNamesRef = useRef(null);

  const {
    data: userRolesData,
    isLoading: isLoadingUserRoles,
    isFetching: isFetchingUserRoles,
    isError: isUserRolesError,
    error: userRolesError,
    refetch: refetchUserRoles,
  } = useGetUserAccessQuery(
    { email },
    { skip: !email, pollingInterval: PERMISSIONS_POLL_INTERVAL_MS, refetchOnReconnect: true }
  );

  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
  } = useGetRolesQuery({ perPage: 200 }, { skip: !email });

  const roleNames = userRolesData?.roleNames || [];

  useEffect(() => {
    if (!rolesData || !userRolesData) return;

    // Only a role set that actually changed from a PREVIOUS successful load
    // counts — the first load has nothing to compare against, and this must
    // not fire on every re-render, only once per real change.
    if (previousRoleNamesRef.current !== null && !sameRoleNames(previousRoleNamesRef.current, roleNames)) {
      dispatch(setSessionExpiredReason("permissions-changed"));
      return;
    }
    previousRoleNamesRef.current = roleNames;

    const rolesByName = new Map(rolesData.roles.map((role) => [role.role_name, role]));
    const permissions = new Set();
    roleNames.forEach((name) => {
      const role = rolesByName.get(name);
      (role?.permissions || []).forEach((p) => permissions.add(p));
    });

    dispatch(setAccessRoleNames(roleNames));
    dispatch(setAccessPermissions(Array.from(permissions)));
  }, [rolesData, userRolesData, roleNames, dispatch]);

  return {
    roleNames,
    // isLoading (RTK Query's own, true only for the very first-ever fetch),
    // deliberately NOT isFetching. Real bug: using isFetching here meant every
    // route guard built on this hook (RequireEcrOpsRole, RequireAccessManagementRole,
    // RequireOrgRole, etc.) swapped its <Outlet /> for a loading spinner on
    // EVERY background refetch — including the poll above and, worse, any
    // mutation anywhere in the app that invalidates the "EffectiveAccess" tag
    // (e.g. deactivating/reactivating/deleting a role assignment on THIS
    // page). Swapping Outlet -> spinner -> Outlet unmounts and remounts the
    // entire routed page, wiping all of its local state — this is what was
    // silently closing RoleAssignmentsPage's group modal (and resetting
    // everything else on the page) after a plain Deactivate/Reactivate,
    // nothing to do with the modal's own logic. A background refetch should
    // update data quietly; it must never cause the page under a route guard
    // to disappear and reappear.
    isLoading: isLoadingUserRoles || isLoadingRoles,
    isError: isUserRolesError,
    error: userRolesError,
    refetch: refetchUserRoles,
  };
};

// Returns true once permissions have loaded and effectively grant `key`
// (directly, or via a covering wildcard like "auth:*"). Returns false (not
// "unknown") while loading, so gated actions stay hidden/disabled until we
// can positively confirm the administrator holds the permission.
export const usePermission = (key) => {
  const permissions = useSelector((state) => state.accessPermissions.permissions);
  return permissionSetGrants(permissions, key);
};

export const usePermissionsLoaded = () => useSelector((state) => state.accessPermissions.loaded);

// The current user's global role names (e.g. ["OperationsAdmin"]), for the
// rare cases that need to gate on a specific role rather than a permission
// (e.g. restricting the whole Access Management section to admin roles).
export const useCurrentUserRoleNames = () => useSelector((state) => state.accessPermissions.roleNames);

// Whether the signed-in user holds `permissionKey` either globally or via ANY
// per-org role (Owner/Approver/Leadership on at least one org). Deliberately
// separate from usePermission/accessPermissions.permissions: that global set
// is built from global roles only (see useAccessPermissions above) and must
// stay that way — folding a per-org role's permissions into it would leak
// org-scoped grants like organization:id:update as if they applied
// everywhere (see orgList.jsx's canEditOrgId, which correctly keeps that
// check per-org). This hook answers a narrower, safe question: "is there
// SOME org, global or not, where the user holds this permission" — right for
// gating a global list page's mere visibility (e.g. /global-orgs), not for
// gating a specific action on a specific org.
export const useHasOrgReadAccessAnywhere = (permissionKey) => {
  const email = useSelector((state) => state.auth?.userAccount?.username);
  const hasGlobalAccess = usePermission(permissionKey);

  const { data: userRolesData, isLoading: isLoadingUserRoles } = useGetUserAccessQuery(
    { email },
    { skip: !email }
  );
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({ perPage: 200 }, { skip: !email });

  const hasPerOrgAccess = (userRolesData?.organization || []).some((org) =>
    roleGrantsPermission(rolesData?.roles, org.role, permissionKey)
  );

  return {
    hasAccess: hasGlobalAccess || hasPerOrgAccess,
    // isLoading only (not isFetching) — see useAccessPermissions's own note
    // just above: gating a route guard's <Outlet /> on isFetching unmounts
    // and remounts the routed page on every background refetch, wiping its
    // local state for no reason.
    isLoading: isLoadingUserRoles || isLoadingRoles,
  };
};
